/**
 * Amounts spelled out in words, as required on Algerian invoices and financial
 * documents ("montant en toutes lettres" / "المبلغ بالحروف").
 *
 * Supports French, Arabic and English so the printed line follows the language
 * the document is issued in.
 */

/* ────────────────────────────── French ────────────────────────────── */

const FR_UNITS = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix',
  'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const FR_TENS = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];

function frBelowHundred(n) {
  if (n < 20) return FR_UNITS[n];

  const ten = Math.floor(n / 10);
  const unit = n % 10;

  if (ten === 7 || ten === 9) {
    const base = FR_TENS[ten];
    const rest = FR_UNITS[10 + unit];
    return unit === 1 && ten === 7 ? `${base} et onze` : `${base}-${rest}`;
  }

  if (unit === 0) return ten === 8 ? 'quatre-vingts' : FR_TENS[ten];
  if (unit === 1 && ten !== 8) return `${FR_TENS[ten]} et un`;

  return `${FR_TENS[ten]}-${FR_UNITS[unit]}`;
}

function frBelowThousand(n) {
  if (n < 100) return frBelowHundred(n);

  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const prefix = hundreds === 1 ? 'cent' : `${FR_UNITS[hundreds]} cent`;

  if (rest === 0) return hundreds === 1 ? 'cent' : `${prefix}s`;
  return `${prefix} ${frBelowHundred(rest)}`;
}

/**
 * "vingt" and "cent" keep their plural s before a noun like million, but drop
 * it before the numeral mille: quatre-vingts millions, quatre-vingt mille.
 */
function frBeforeMille(text) {
  return text.replace(/(vingt|cent)s$/, '$1');
}

function frSpell(n) {
  if (n === 0) return 'zéro';

  const parts = [];
  const billions = Math.floor(n / 1000000000);
  const millions = Math.floor((n % 1000000000) / 1000000);
  const thousands = Math.floor((n % 1000000) / 1000);
  const rest = n % 1000;

  if (billions) parts.push(`${frBelowThousand(billions)} milliard${billions > 1 ? 's' : ''}`);
  if (millions) parts.push(`${frBelowThousand(millions)} million${millions > 1 ? 's' : ''}`);
  if (thousands) parts.push(thousands === 1 ? 'mille' : `${frBeforeMille(frBelowThousand(thousands))} mille`);
  if (rest) parts.push(frBelowThousand(rest));

  return parts.join(' ');
}

/* ────────────────────────────── English ───────────────────────────── */

const EN_UNITS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function enBelowHundred(n) {
  if (n < 20) return EN_UNITS[n];
  const ten = Math.floor(n / 10);
  const unit = n % 10;
  return unit === 0 ? EN_TENS[ten] : `${EN_TENS[ten]}-${EN_UNITS[unit]}`;
}

function enBelowThousand(n) {
  if (n < 100) return enBelowHundred(n);
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  return rest === 0
    ? `${EN_UNITS[hundreds]} hundred`
    : `${EN_UNITS[hundreds]} hundred and ${enBelowHundred(rest)}`;
}

function enSpell(n) {
  if (n === 0) return 'zero';

  const parts = [];
  const billions = Math.floor(n / 1000000000);
  const millions = Math.floor((n % 1000000000) / 1000000);
  const thousands = Math.floor((n % 1000000) / 1000);
  const rest = n % 1000;

  if (billions) parts.push(`${enBelowThousand(billions)} billion`);
  if (millions) parts.push(`${enBelowThousand(millions)} million`);
  if (thousands) parts.push(`${enBelowThousand(thousands)} thousand`);
  if (rest) parts.push(parts.length && rest < 100 ? `and ${enBelowThousand(rest)}` : enBelowThousand(rest));

  return parts.join(' ');
}

/* ────────────────────────────── Arabic ────────────────────────────── */

const AR_UNITS = ['صفر', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة', 'عشرة',
  'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
const AR_TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const AR_HUNDREDS = ['', 'مئة', 'مئتان', 'ثلاثمئة', 'أربعمئة', 'خمسمئة', 'ستمئة', 'سبعمئة', 'ثمانمئة', 'تسعمئة'];

function arBelowHundred(n) {
  if (n < 20) return AR_UNITS[n];
  const ten = Math.floor(n / 10);
  const unit = n % 10;
  /** Arabic says the unit before the ten, joined by "و": واحد وعشرون. */
  return unit === 0 ? AR_TENS[ten] : `${AR_UNITS[unit]} و${AR_TENS[ten]}`;
}

function arBelowThousand(n) {
  if (n < 100) return arBelowHundred(n);
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  return rest === 0 ? AR_HUNDREDS[hundreds] : `${AR_HUNDREDS[hundreds]} و${arBelowHundred(rest)}`;
}

/** Arabic counted nouns change with the count: 1, 2, 3-10, then 11+. */
function arScale(count, [singular, dual, plural, accusative]) {
  if (count === 1) return singular;
  if (count === 2) return dual;
  if (count >= 3 && count <= 10) return `${arBelowThousand(count)} ${plural}`;
  return `${arBelowThousand(count)} ${accusative}`;
}

function arSpell(n) {
  if (n === 0) return 'صفر';

  const parts = [];
  const billions = Math.floor(n / 1000000000);
  const millions = Math.floor((n % 1000000000) / 1000000);
  const thousands = Math.floor((n % 1000000) / 1000);
  const rest = n % 1000;

  if (billions) parts.push(arScale(billions, ['مليار', 'ملياران', 'مليارات', 'مليارًا']));
  if (millions) parts.push(arScale(millions, ['مليون', 'مليونان', 'ملايين', 'مليونًا']));
  if (thousands) parts.push(arScale(thousands, ['ألف', 'ألفان', 'آلاف', 'ألفًا']));
  if (rest) parts.push(arBelowThousand(rest));

  return parts.join(' و');
}

/* ───────────────────────────── Currency ───────────────────────────── */

const CURRENCY_WORDS = {
  fr: {
    dzd: { one: 'dinar algérien', many: 'dinars algériens', sub: { one: 'centime', many: 'centimes' } },
    usd: { one: 'dollar américain', many: 'dollars américains', sub: { one: 'cent', many: 'cents' } },
    eur: { one: 'euro', many: 'euros', sub: { one: 'centime', many: 'centimes' } },
  },
  en: {
    dzd: { one: 'Algerian dinar', many: 'Algerian dinars', sub: { one: 'centime', many: 'centimes' } },
    usd: { one: 'US dollar', many: 'US dollars', sub: { one: 'cent', many: 'cents' } },
    eur: { one: 'euro', many: 'euros', sub: { one: 'cent', many: 'cents' } },
  },
  ar: {
    dzd: { scale: ['دينار جزائري', 'ديناران جزائريان', 'دنانير جزائرية', 'دينارًا جزائريًا'], sub: 'سنتيم' },
    usd: { scale: ['دولار أمريكي', 'دولاران أمريكيان', 'دولارات أمريكية', 'دولارًا أمريكيًا'], sub: 'سنت' },
    eur: { scale: ['يورو', 'يوروان', 'يوروات', 'يورو'], sub: 'سنت' },
  },
};

const SPELLERS = { fr: frSpell, en: enSpell, ar: arSpell };

/**
 * Spells an amount in the given language.
 *
 * @param {number|string} value    the amount
 * @param {string}        language 'fr' | 'ar' | 'en' (anything else falls back to French)
 * @param {string}        currency ISO code, e.g. 'dzd'
 */
export function amountInWords(value, language = 'fr', currency = 'dzd') {
  const lang = SPELLERS[language] ? language : 'fr';
  const code = String(currency || 'dzd').toLowerCase();
  const spell = SPELLERS[lang];

  const absolute = Math.abs(Number(value) || 0);
  const whole = Math.floor(absolute);
  const cents = Math.round((absolute - whole) * 100);

  const words = CURRENCY_WORDS[lang][code] || CURRENCY_WORDS[lang].dzd;

  if (lang === 'ar') {
    /**
     * The counted noun agrees with the last two digits: 1 and 2 have their own
     * forms, 3-10 take the plural, everything else the accusative singular.
     */
    const noun = (n) => {
      if (n === 1) return words.scale[0];
      if (n === 2) return words.scale[1];
      const lastTwo = n % 100;
      /** After a round hundred or thousand the noun stays singular: مئة دينار. */
      if (lastTwo === 0) return words.scale[0];
      return lastTwo >= 3 && lastTwo <= 10 ? words.scale[2] : words.scale[3];
    };

    /** For 1 and 2 the noun already carries the count, so the numeral is dropped. */
    const mainText = whole === 1 || whole === 2
      ? noun(whole)
      : `${spell(whole)} ${noun(whole)}`;

    return cents > 0 ? `${mainText} و${spell(cents)} ${words.sub}` : mainText;
  }

  /** French keeps the singular after zéro and un; English pluralises from zero. */
  const singular = lang === 'fr' ? whole <= 1 : whole === 1;
  const main = `${spell(whole)} ${singular ? words.one : words.many}`;

  if (cents === 0) return main;

  const subUnit = cents === 1 ? words.sub.one : words.sub.many;
  const joiner = lang === 'fr' ? 'et' : 'and';

  return `${main} ${joiner} ${spell(cents)} ${subUnit}`;
}

/**
 * The introductory sentence Algerian invoices carry above the amount in words.
 */
export const amountInWordsPrefix = (language = 'fr') => ({
  fr: 'Arrêtée la présente facture à la somme de :',
  ar: 'أوقفت هذه الفاتورة على مبلغ :',
  en: 'This invoice is stopped at the sum of:',
}[language] ?? 'Arrêtée la présente facture à la somme de :');
